import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import PageHead from "../components/PageHead";
import RunPipelineButton from "../components/RunPipelineButton";
import { toast } from "sonner";
import { fmtDateTime } from "../lib/signals";
import { PaperPlaneRight, TelegramLogo, WhatsappLogo, RedditLogo, NotePencil, CheckCircle, Clock } from "@phosphor-icons/react";

const SRC = ["all", "telegram", "whatsapp", "reddit", "manual"];
const SRC_ICON = { telegram: TelegramLogo, whatsapp: WhatsappLogo, reddit: RedditLogo, manual: NotePencil };
const SRC_COLOR = { telegram: "text-sky-500", whatsapp: "text-bull", reddit: "text-orange-500", manual: "text-brand" };

export default function Messages() {
  const [rows, setRows] = useState([]);
  const [src, setSrc] = useState("all");
  const [text, setText] = useState("");
  const [group, setGroup] = useState("Manual Entry");

  const load = () => {
    const params = src === "all" ? {} : { source: src };
    api.get("/messages", { params }).then((r) => setRows(r.data.messages)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [src]);

  const addManual = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/messages/manual", { group_name: group, text });
      toast.success("Added to queue");
      setText(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHead title="Message Feed" subtitle="Every ingested call — newest first" right={<RunPipelineButton onDone={load} />} />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        <div>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {SRC.map((sname) => (
              <button key={sname} data-testid={`msg-filter-${sname}`} onClick={() => setSrc(sname)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium capitalize transition-all duration-200 ${src === sname ? "brand-gradient text-white" : "glass text-muted-foreground hover:text-foreground"}`}>{sname}</button>
            ))}
            <span className="ml-auto text-xs text-muted-foreground font-mono">{rows.length}</span>
          </div>
          <div className="glass-strong rounded-3xl divide-y divide-border/50 max-h-[70vh] overflow-y-auto">
            {rows.map((m) => {
              const Icon = SRC_ICON[m.source] || NotePencil;
              return (
                <div key={m.id} data-testid={`message-${m.id}`} className="p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-2xl bg-foreground/5 flex items-center justify-center shrink-0">
                    <Icon size={16} weight="bold" className={SRC_COLOR[m.source] || "text-muted-foreground"} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{m.group_name}</span>
                      <span className="text-xs text-muted-foreground">· {m.sender}</span>
                      {m.processed
                        ? <span className="flex items-center gap-1 text-[10px] text-bull"><CheckCircle size={11} weight="fill" /> parsed</span>
                        : <span className="flex items-center gap-1 text-[10px] text-neutral"><Clock size={11} weight="fill" /> pending</span>}
                      <span className="text-[10px] text-muted-foreground ml-auto font-mono">{fmtDateTime(m.timestamp)}</span>
                    </div>
                    <div className="text-sm mt-0.5 break-words">{m.text}</div>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">No messages.</div>}
          </div>
        </div>

        <div className="glass-strong rounded-3xl p-5 h-fit">
          <h3 className="font-display font-bold mb-1">Paste Calls</h3>
          <p className="text-xs text-muted-foreground mb-3">Copy calls from any group. One per line — parsed on the next pipeline run.</p>
          <input data-testid="manual-group" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="Group name"
            className="w-full rounded-2xl bg-background/60 border border-border px-4 py-2.5 text-sm outline-none focus:border-brand mb-3 transition-colors duration-200" />
          <textarea data-testid="manual-text" value={text} onChange={(e) => setText(e.target.value)} rows={6}
            placeholder="RELIANCE buy above 2850 SL 2790 tgt 2950…"
            className="w-full rounded-2xl bg-background/60 border border-border px-4 py-2.5 text-sm outline-none focus:border-brand resize-none mb-3 transition-colors duration-200" />
          <button data-testid="manual-submit" onClick={addManual}
            className="w-full brand-gradient text-white font-semibold text-sm rounded-2xl py-2.5 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity duration-200">
            <PaperPlaneRight size={15} weight="fill" /> Add to Queue
          </button>
        </div>
      </div>
    </>
  );
}
